/**
 * Publication Sync From Channel Renderer
 *
 * Displays sync-from-channel batch process details:
 * operation description and source channels grouped by connector.
 *
 * Data shape (from PublicationSyncFromChannelBatchProcessStartedEventSchema):
 *   publications: Array<{ tesellyPublicationId, connectorId, integrationUserId, channelPublicationId }>
 *   totalItems: number
 */

import { FC } from 'react';
import { Group, Stack, Text, ThemeIcon } from '@mantine/core';
import { IconPlug } from '@tabler/icons-react';
import type { DetailRendererProps, OperationRendererConfig, ProcessData } from './index';

type SyncPublication = {
  connectorId: string;
  integrationUserId: string;
};

const DetailComponent: FC<DetailRendererProps> = ({ process, resolved, t }) => {
  const data = ( process.data || {}) as ProcessData;
  const publications = ( data.publications || []) as SyncPublication[];

  const channelCounts = new Map<string, number>();
  for ( const pub of publications ) {
    channelCounts.set( pub.connectorId, ( channelCounts.get( pub.connectorId ) ?? 0 ) + 1 );
  }

  return (
    <Stack gap="sm">
      <Text fz="sm" c="dimmed">
        {t( 'batchProcesses_operations_PublicationSyncFromChannel_description' )}
      </Text>

      {channelCounts.size > 0 && (
        <Stack gap="xs">
          <Text fz="sm" fw={500}>
            {t( 'batchProcesses_renderer_sourceChannel' )}:
          </Text>
          {Array.from( channelCounts.entries()).map(([ connectorId, count ]) => {
            const resolvedChannel = resolved.resolvedChannels[connectorId];
            return (
              <Group key={connectorId} gap="xs">
                <ThemeIcon size="sm" variant="light" color="gray">
                  <IconPlug size={14} />
                </ThemeIcon>
                <Text fz="sm">
                  {resolvedChannel?.name ?? connectorId}
                </Text>
                <Text fz="sm" c="dimmed">
                  ({count})
                </Text>
              </Group>
            );
          })}
        </Stack>
      )}
    </Stack>
  );
};

export const config: Omit<OperationRendererConfig, 'icon' | 'color' | 'labelKey'> = {
  getSummary: ( data, t, _process, resolvedChannels ) => {
    const publications = ( data?.publications || []) as SyncPublication[];

    const connectorIds = new Set( publications.map(( p ) => p.connectorId ));

    if ( connectorIds.size === 1 ) {
      const connectorId = connectorIds.values().next().value;
      const resolved = resolvedChannels?.[connectorId!];
      const channelName = resolved?.name ?? connectorId;
      return t( 'batchProcesses_summary_fromChannel', { channel: channelName });
    }

    if ( connectorIds.size > 1 ) {
      return t( 'batchProcesses_summary_multipleChannels', {
        count: connectorIds.size,
        defaultValue: '{{count}} channels'
      });
    }

    return t( 'batchProcesses_summary_unknownOperation' );
  },
  DetailComponent,
  getRequiredReferenceIds: ( process ) => {
    const data = ( process?.data || {}) as ProcessData;
    const publications = ( data.publications || []) as SyncPublication[];
    const channelIds = [...new Set( publications.map(( p ) => p.connectorId ))];
    return { channelIds };
  }
};

export default DetailComponent;
